var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import StorefrontIcon from '@mui/icons-material/Storefront';
import TuneIcon from '@mui/icons-material/Tune';
import WebhookIcon from '@mui/icons-material/Webhook';
import { Box, Step, StepLabel, Stepper, styled } from '@mui/material';
import { lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../context/Settings';
import { COLORS, iconStyles, SIZES, styles } from './theme';
var Heading = lazy(function () { return import('../../components/UIcomponents/Heading'); });
var SettingsProgress = function (_a) {
    var shopId = _a.shopId;
    var t = useTranslation('settings').t;
    var _b = useSettings(), shopSettings = _b.shopSettings, setShopSettings = _b.setShopSettings;
    useEffect(function () {
        if (shopId) {
            setShopSettings(function (prev) { return (__assign(__assign({}, prev), { isShopAdded: true })); });
        }
    }, [shopId, setShopSettings]);
    var isAllConfigured = shopSettings.isShopAdded && shopSettings.isWebhookAdded && shopSettings.isExtraSettingsAdded;
    var ColorlibStepIcon = function (props) {
        var active = props.active, completed = props.completed, className = props.className;
        var icons = {
            1: _jsx(StorefrontIcon, { sx: iconStyles }),
            2: _jsx(WebhookIcon, { sx: iconStyles }),
            3: _jsx(TuneIcon, { sx: iconStyles }),
        };
        return (_jsx(ColorlibStepIconRoot, { ownerState: { completed: completed, active: active }, className: className, children: icons[String(props.icon)] }));
    };
    var ColorlibStepIconRoot = styled('div', {
        shouldForwardProp: function (prop) { return prop !== 'ownerState'; },
    })(function (_a) {
        var ownerState = _a.ownerState;
        return (__assign(__assign({ backgroundColor: COLORS.inactive, zIndex: 1, color: COLORS.inactiveText, width: SIZES.stepIconSize, height: SIZES.stepIconSize, display: 'flex', borderRadius: '50%', justifyContent: 'center', alignItems: 'center', fontSize: '1.2rem', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', border: 'none', position: 'relative', overflow: 'hidden', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', willChange: 'transform, background-image', backfaceVisibility: 'hidden' }, (ownerState.active && {
            backgroundImage: COLORS.gradient,
            boxShadow: "0 4px 20px 0 ".concat(COLORS.shadow),
            color: COLORS.white,
            transform: 'scale(1.05)',
        })), (ownerState.completed && {
            backgroundImage: COLORS.gradient,
            color: COLORS.white,
        })));
    });
    var steps = [t('steps.addShop'), t('steps.addWebhook'), t('steps.extraSettings')];
    var CustomConnector = function (_a) {
        var index = _a.index;
        var isCompleted = (index === 0 && shopSettings.isWebhookAdded) || (index === 1 && shopSettings.isExtraSettingsAdded);
        var connectorStyle = {
            position: 'absolute !important',
            top: '25px !important',
            left: 'calc(-50% + 20px) !important',
            right: 'calc(50% + 20px) !important',
            zIndex: '0 !important',
            display: 'block !important',
            visibility: 'visible !important',
            opacity: '1 !important',
        };
        var lineStyle = {
            height: '3px !important',
            border: '0 !important',
            backgroundColor: isCompleted ? 'transparent !important' : '#e0e0e0 !important',
            backgroundImage: isCompleted ? 'linear-gradient(98deg, rgb(198, 167, 254), rgb(145, 85, 253) 94%) !important' : 'none !important',
            borderRadius: '2px !important',
            margin: '0 !important',
            padding: '0 !important',
            position: 'relative !important',
            top: '0 !important',
            left: '0 !important',
            right: '0 !important',
            bottom: '0 !important',
            transform: 'none !important',
            minHeight: '3px !important',
            maxHeight: '3px !important',
            width: '100% !important',
            display: 'block !important',
            visibility: 'visible !important',
            opacity: '1 !important',
            flex: 'none !important',
        };
        return (_jsx("div", { style: connectorStyle, className: "MuiStepConnector-root MuiStepConnector-horizontal", children: _jsx("span", { style: lineStyle, className: "MuiStepConnector-line MuiStepConnector-lineHorizontal" }) }));
    };
    return (_jsxs(Box, { sx: __assign(__assign({}, styles.container), { 
            // Force CSS containment and isolation
            isolation: 'isolate', contain: 'layout style', '& .MuiStepper-root.MuiStepper-horizontal.MuiStepper-alternativeLabel': {
                position: 'relative !important',
                '& .MuiStepConnector-root.MuiStepConnector-horizontal': {
                    position: 'absolute !important',
                    top: "".concat(SIZES.stepIconSize / 2, "px !important"),
                    left: 'calc(-50% + 20px) !important',
                    right: 'calc(50% + 20px) !important',
                    zIndex: 0,
                    '& .MuiStepConnector-line.MuiStepConnector-lineHorizontal': {
                        height: "".concat(SIZES.connectorHeight, "px !important"),
                        border: '0 !important',
                        backgroundColor: "".concat(COLORS.connectorInactive, " !important"),
                        borderRadius: "".concat(SIZES.connectorBorderRadius, "px !important"),
                        position: 'relative !important',
                        top: '0 !important',
                        transform: 'none !important',
                    },
                },
            } }), className: "settings-progress-wrapper", children: [_jsx(Heading, { title: isAllConfigured ? 'steps.allConfigured' : 'steps.title' }), _jsx(Stepper, { alternativeLabel: true, activeStep: -1, className: "settings-progress-stepper", connector: _jsx(CustomConnector, { index: 0 }), children: steps.map(function (label, index) { return (_jsx(Step, { completed: (index === 0 && shopSettings.isShopAdded) ||
                        (index === 1 && shopSettings.isWebhookAdded) ||
                        (index === 2 && shopSettings.isExtraSettingsAdded), children: _jsx(StepLabel, { StepIconComponent: ColorlibStepIcon, children: label }) }, label)); }) })] }));
};
export default SettingsProgress;
